// System console: installation settings (owner instruction PD-710; settings stream PD-665 – PD-689).
// Grouped forms generated from the settings registry: current value, built-in default, whether it is
// still the default, reset to default, server validation messages, write-only secrets and a
// confirmation (reason + acknowledgement) for sensitive switches. Read-only facts that still come
// from the environment are listed at the end. The server enforces every rule; this page only asks.
import type { EnvironmentFactsDto, InstallationSettingDto, InstallationSettingsResponse, SettingGroup } from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiRequestError, api, get } from '../api.ts';
import { Dialog, ErrorState, ErrorSummary, Field, Loading, PageHead } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';

const P = '/api/platform/v1/settings';
const KEY = ['platform', 'settings'];

type Draft = string | boolean | string[];

function draftOf(i: InstallationSettingDto): Draft {
  switch (i.input.kind) {
    case 'boolean':
      return i.value === true;
    case 'list':
      return Array.isArray(i.value) ? (i.value as string[]) : [];
    case 'secret':
      return '';
    default:
      return i.value === null || i.value === undefined ? '' : String(i.value);
  }
}

/** The JSON value a draft stands for (integers as numbers; bad text is sent as is and refused by the server). */
function valueOf(i: InstallationSettingDto, d: Draft): unknown {
  if (i.input.kind === 'integer') return typeof d === 'string' && /^-?\d+$/.test(d.trim()) ? Number(d.trim()) : d;
  if (i.input.kind === 'text') return typeof d === 'string' && d.trim() === '' && i.input.nullable ? null : d;
  return d;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function PlatformSettingsPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => get<InstallationSettingsResponse>(P) });
  const [changedOnly, setChangedOnly] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: KEY });
  return (
    <>
      <PageHead eyebrow={t('platform.eyebrow')} title={t('set.title')} intro={t('set.intro')} />
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : (
        <>
          <div className="toolbar">
            <label>
              {t('set.filter')}{' '}
              <select value={changedOnly ? 'changed' : 'all'} onChange={(e) => setChangedOnly(e.target.value === 'changed')}>
                <option value="all">{t('set.filter.all')}</option>
                <option value="changed">{t('set.filter.changed')}</option>
              </select>
            </label>
            <span className="tiny">{t('set.version', { version: q.data.version })}</span>
          </div>
          {q.data.groups.map((g) => (
            <SettingsGroup key={g} group={g} items={q.data.items.filter((i) => i.group === g && (!changedOnly || !i.isDefault))} onChanged={refresh} />
          ))}
          <EnvironmentFacts env={q.data.environment} />
        </>
      )}
    </>
  );
}

function SettingsGroup({ group, items, onChanged }: { group: SettingGroup; items: readonly InstallationSettingDto[]; onChanged: () => void }) {
  const { t } = useI18n();
  if (!items.length) return null;
  const id = `set-g-${group}`;
  return (
    <section className="panel" aria-labelledby={id}>
      <div className="panel-head">
        <h2 id={id}>{t(`set.g.${group}` as never)}</h2>
      </div>
      <div className="pad stack">
        {items.map((i) => (
          <SettingRow key={i.key} item={i} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}

function SettingRow({ item, onChanged }: { item: InstallationSettingDto; onChanged: () => void }) {
  const { t, tk, dateTime } = useI18n();
  const [draft, setDraft] = useState<Draft>(() => draftOf(item));
  // A newer version (saved here or elsewhere) replaces the draft; the "saved" notice stays visible.
  const [seen, setSeen] = useState(item.version);
  if (seen !== item.version) {
    setSeen(item.version);
    setDraft(draftOf(item));
  }
  const [confirming, setConfirming] = useState<null | { kind: 'save' | 'reset' | 'clear' }>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const label = tk(`set.k.${item.key}.label`);
  const fid = `set-${item.key.replace(/\./g, '-')}`;
  const format = useFormat();

  const send = useMutation({
    mutationFn: (v: { kind: 'save' | 'reset' | 'clear'; reason?: string }) => {
      const confirm = item.sensitive ? { reason: v.reason, confirmed: true } : {};
      if (v.kind === 'reset') return api<InstallationSettingDto>('POST', `${P}/${item.key}/reset`, { expectedVersion: item.version, ...confirm });
      if (item.secret) return api<InstallationSettingDto>('PUT', `${P}/${item.key}`, { expectedVersion: item.version, secret: v.kind === 'clear' ? { kind: 'clear' } : { kind: 'set', value: draft }, ...confirm });
      return api<InstallationSettingDto>('PUT', `${P}/${item.key}`, { expectedVersion: item.version, value: valueOf(item, draft), ...confirm });
    },
    onSuccess: (_r, v) => {
      setConfirming(null);
      setNotice(t(v.kind === 'reset' ? 'set.resetDone' : 'set.saved'));
      onChanged();
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.status === 409) onChanged();
    },
  });
  const fe = send.error instanceof ApiRequestError ? send.error.fields : {};
  const conflict = send.error instanceof ApiRequestError && send.error.status === 409;
  const dirty = item.secret ? draft !== '' : !same(valueOf(item, draft), item.value);
  const start = (kind: 'save' | 'reset' | 'clear') => {
    setNotice(null);
    if (item.sensitive) setConfirming({ kind });
    else send.mutate({ kind });
  };
  const input = item.input;
  const hint = [
    input.kind === 'integer' ? t('set.range', { min: input.min, max: input.max }) : null,
    input.kind === 'integer' && input.productionMin !== undefined ? t('set.productionMin', { min: input.productionMin }) : null,
    item.secret ? t('set.secret.help') : null,
  ].filter(Boolean).join(' · ');

  return (
    <form
      className="setting"
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        start('save');
      }}
    >
      <ErrorSummary error={conflict ? null : send.error} fieldIds={{ value: fid, secret: fid }} />
      {conflict ? (
        <div className="note warning" role="alert">
          {t('set.conflict')}
        </div>
      ) : null}
      <Field id={fid} label={label} hint={[tk(`set.k.${item.key}.help`), hint].filter(Boolean).join(' ')} error={fe.value ?? fe.secret} full>
        {(p) => <SettingInputControl item={item} draft={draft} setDraft={setDraft} props={p} />}
      </Field>
      <p className="tiny">
        <span className={`badge ${item.isDefault ? '' : 'blue'}`}>{t(item.isDefault ? 'set.isDefault' : 'set.changed')}</span>{' '}
        {item.secret ? t(item.secretSet ? 'set.secret.set' : 'set.secret.notSet') : t('set.default', { value: format(item, item.defaultValue) })}
        {' · '}
        {t(`set.source.${item.source}` as never)}
        {item.updatedAt ? ` · ${item.updatedBy ? t('set.updated', { at: dateTime(item.updatedAt), by: item.updatedBy }) : item.source === 'environment' ? t('set.updatedSystem', { at: dateTime(item.updatedAt) }) : t('set.updatedAt', { at: dateTime(item.updatedAt) })}` : ''}
        {' · '}
        {t(`set.usedBy.${item.consumer}` as never)}
        {item.sensitive ? ` · ${t('set.sensitive')}` : ''}
        {item.environmentVariable ? (
          <>
            {' · '}
            {t('set.replaces', { name: '⁨' + item.environmentVariable + '⁩' })}
          </>
        ) : null}
      </p>
      {item.storedProblem ? (
        <div className="note warning">{t('set.storedProblem', { problem: tk(item.storedProblem.code, item.storedProblem.params) })}</div>
      ) : null}
      {notice ? (
        <p className="tiny" role="status">
          {notice}
        </p>
      ) : null}
      <div className="form-actions">
        <button className="button primary small" type="submit" disabled={!dirty || send.isPending}>
          {item.secret ? t('set.secret.replace') : t('set.save')}
        </button>
        {item.secret && item.secretSet ? (
          <button className="button small" type="button" disabled={send.isPending} onClick={() => start('clear')}>
            {t('set.secret.clear')}
          </button>
        ) : null}
        {!item.secret && item.source !== 'default' ? (
          <button className="button quiet small" type="button" disabled={send.isPending} onClick={() => start('reset')}>
            {t('set.reset')}
          </button>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirming !== null}
        label={label}
        from={item.secret ? t(item.secretSet ? 'set.secret.set' : 'set.secret.notSet') : format(item, item.value)}
        to={item.secret ? (confirming?.kind === 'clear' ? t('set.secret.notSet') : t('set.secret.set')) : confirming?.kind === 'reset' ? format(item, item.defaultValue) : format(item, valueOf(item, draft))}
        error={send.error}
        pending={send.isPending}
        onClose={() => setConfirming(null)}
        onConfirm={(reason) => confirming && send.mutate({ kind: confirming.kind, reason })}
      />
    </form>
  );
}

function SettingInputControl({ item, draft, setDraft, props }: { item: InstallationSettingDto; draft: Draft; setDraft: (d: Draft) => void; props: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string } }) {
  const { t, tk } = useI18n();
  const input = item.input;
  switch (input.kind) {
    case 'boolean':
      return (
        <select {...props} value={draft === true ? 'true' : 'false'} onChange={(e) => setDraft(e.target.value === 'true')}>
          <option value="true">{t('set.on')}</option>
          <option value="false">{t('set.off')}</option>
        </select>
      );
    case 'enum':
      return (
        <select {...props} value={String(draft)} onChange={(e) => setDraft(e.target.value)}>
          {input.options.map((o) => (
            <option key={o} value={o}>
              {tk(`set.o.${o}`, undefined, o)}
            </option>
          ))}
        </select>
      );
    case 'list': {
      const selected = Array.isArray(draft) ? draft : [];
      return (
        <div {...props} role="group">
          {input.options.map((o) => (
            <label key={o} className="checkline">
              <input type="checkbox" checked={selected.includes(o)} onChange={(e) => setDraft(e.target.checked ? [...selected, o] : selected.filter((x) => x !== o))} /> {tk(`set.o.${o}`, undefined, o)}
            </label>
          ))}
        </div>
      );
    }
    case 'secret':
      return <input {...props} type="password" autoComplete="new-password" dir="ltr" placeholder={t('set.secret.new')} value={String(draft)} onChange={(e) => setDraft(e.target.value)} />;
    case 'integer':
      return (
        <span className="inline-unit">
          <input {...props} inputMode="numeric" dir="ltr" value={String(draft)} onChange={(e) => setDraft(e.target.value)} />
          {input.unit !== 'count' ? <span className="tiny"> {tk(`set.unit.${input.unit}`)}</span> : null}
        </span>
      );
    case 'text':
      return <input {...props} dir="ltr" maxLength={input.maxLength} value={String(draft)} onChange={(e) => setDraft(e.target.value)} />;
  }
}

/** A value as the operator reads it (units, option labels, On/Off). */
function useFormat() {
  const { t, tk } = useI18n();
  return (item: InstallationSettingDto, v: unknown): string => {
    if (v === null || v === undefined || v === '') return t('set.none');
    const input = item.input;
    if (input.kind === 'boolean') return t(v === true ? 'set.on' : 'set.off');
    if (input.kind === 'enum') return tk(`set.o.${String(v)}`, undefined, String(v));
    if (input.kind === 'list') return Array.isArray(v) && v.length ? v.map((o) => tk(`set.o.${String(o)}`, undefined, String(o))).join(', ') : t('set.none');
    if (input.kind === 'integer') return input.unit === 'count' ? String(v) : `${String(v)} ${tk(`set.unit.${input.unit}`)}`;
    return String(v);
  };
}

function ConfirmDialog(p: { open: boolean; label: string; from: string; to: string; error: unknown; pending: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [understood, setUnderstood] = useState(false);
  const fe = p.error instanceof ApiRequestError ? p.error.fields : {};
  return (
    <Dialog open={p.open} title={t('set.confirm.title', { label: p.label })} subtitle={t('set.confirm.body')} onClose={p.onClose}>
      <div className="stack">
        <p className="tiny">{t('set.confirm.from', { value: p.from })}</p>
        <p className="tiny">
          <strong>{t('set.confirm.to', { value: p.to })}</strong>
        </p>
        <Field label={t('set.confirm.reason')} error={fe.reason} full>
          {(f) => <input {...f} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <label className="checkline">
          <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} /> {t('set.confirm.check')}
        </label>
        <div className="form-actions">
          <button className="button primary" type="button" disabled={!understood || reason.trim().length < 3 || p.pending} onClick={() => p.onConfirm(reason)}>
            {t('set.confirm.apply')}
          </button>
          <button className="button" type="button" onClick={p.onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function EnvironmentFacts({ env }: { env: EnvironmentFactsDto }) {
  const { t, tk } = useI18n();
  const yes = (b: boolean) => t(b ? 'set.env.yes' : 'set.env.no');
  const list = (xs: readonly string[]): ReactNode => (xs.length ? xs.map((x) => <div key={x} data-ltr>{x}</div>) : t('set.env.noneListed'));
  const rows: Array<[string, ReactNode]> = [
    [t('set.env.profile'), tk(`ops.pid.${env.deploymentProfile}`, undefined, env.deploymentProfile)],
    [t('set.env.nodeEnv'), <span data-ltr>{env.nodeEnv}</span>],
    [t('set.env.version'), <span data-ltr>{env.appVersion ?? '—'}</span>],
    [t('set.env.consoleHosts'), list(env.consoleHostnames)],
    [t('set.env.scheme'), <span data-ltr>{`${env.publicScheme} · ${env.publicPort ?? t('set.env.defaultPort')}`}</span>],
    [t('set.env.origins'), list(env.allowedOrigins)],
    [t('set.env.clamav'), yes(env.clamavWired)],
    [t('set.env.keyRing'), yes(env.sealingKeyRings.identity)],
    [t('set.env.ignored'), list(env.ignoredVariables)],
    [t('set.env.retired'), list(env.retiredVariables)],
    [t('set.env.demo'), t(env.demo ? 'set.env.demoOn' : 'set.env.demoOff')], // PD-720
  ];
  return (
    <section className="panel" aria-labelledby="set-env">
      <div className="panel-head">
        <div>
          <h2 id="set-env">{t('set.env.title')}</h2>
          <p>{t('set.env.intro')}</p>
        </div>
      </div>
      <div className="pad">
        <dl className="definition">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        {env.excludedHostnames.map((x) => (
          <div key={x.hostname} className="note warning">
            {t('set.env.excluded', { hostname: x.hostname, reason: x.reason })}
          </div>
        ))}
      </div>
    </section>
  );
}
